import React, { createRef, forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useState, } from 'react';
import { render } from 'react-dom';
import { TooltipManager } from 'choerodon-ui/shared';
import { TooltipContainerRef, TooltipManagerType } from 'choerodon-ui/shared/tooltip-manager';
import ConfigContext from 'choerodon-ui/lib/config-provider/ConfigContext';
import { getProPrefixCls } from 'choerodon-ui/lib/configure/utils';
import { getGlobalPopupContainer } from 'choerodon-ui/lib/trigger/Popup';
import Tooltip from './Tooltip';
import TaskRunner from '../_util/TaskRunner';
export const suffixCls = 'singleton-tooltip';
export { TooltipContainerRef, TooltipManagerType };
const TooltipContainer = forwardRef((_, ref) => {
    const task = useMemo(() => new TaskRunner(), []);
    const [tooltipProps, setTooltipProps] = useState();
    const { getTooltipTheme, getTooltipPlacement } = useContext(ConfigContext);
    const open = useCallback((target, args, duration = 100) => {
        task.cancel();
        task.delay(duration, () => {
            setTooltipProps({
                theme: getTooltipTheme(),
                placement: getTooltipPlacement(),
                ...args,
                hidden: false,
                getRootDomNode: () => target,
            });
        });
        return task;
    }, [task, getTooltipTheme, getTooltipPlacement]);
    const close = useCallback((duration = 100) => {
        task.cancel();
        task.delay(duration, () => {
            setTooltipProps((preState) => ({
                ...preState,
                hidden: true,
            }));
        });
        return task;
    }, [task]);
    const handlePopupMouseEnter = useCallback(() => {
        task.cancel();
    }, [task]);
    const handlePopupMouseLeave = useCallback(() => {
        close();
    }, [close]);
    useImperativeHandle(ref, () => ({
        open,
        close,
    }), [open, close]);
    return (React.createElement(Tooltip, Object.assign({}, tooltipProps, { onPopupMouseEnter: handlePopupMouseEnter, onPopupMouseLeave: handlePopupMouseLeave })));
});
function getRoot() {
    let { root } = TooltipManager;
    if (typeof window !== 'undefined') {
        const popupContainer = getGlobalPopupContainer();
        const doc = window.document;
        if (root) {
            if (!root.parentNode) {
                popupContainer.appendChild(root);
            }
        }
        else {
            root = doc.createElement('div');
            root.className = `${getProPrefixCls(suffixCls)}-container`;
            popupContainer.appendChild(root);
            TooltipManager.root = root;
        }
    }
    return root;
}
async function getContainer() {
    const { container } = TooltipManager;
    if (container) {
        return container;
    }
    return new Promise(resolve => {
        const root = getRoot();
        if (root) {
            const ref = createRef();
            TooltipManager.container = ref;
            render(React.createElement(TooltipContainer, { ref: ref }), root, () => resolve(ref));
        }
    });
}
export async function show(target, props, duration) {
    const container = await getContainer();
    const { current } = container;
    if (current) {
        return current.open(target, props, duration);
    }
}
export async function hide(duration) {
    const container = await getContainer();
    const { current } = container;
    if (current) {
        return current.close(duration);
    }
}
